import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../auth';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://getclearance-production.up.railway.app/api/v1';

const PRIMITIVES = [
  { id: 'screening.individual', label: 'Screening' },
  { id: 'biometrics.face_compare', label: 'Face Match' },
  { id: 'document.ocr', label: 'Document OCR' },
  { id: 'device_intel.ip_check', label: 'IP Check' },
  { id: 'device_intel.email_check', label: 'Email Check' },
  { id: 'compliance.address_verify', label: 'Address Verify' },
];

function formatTimestamp(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatCost(costCents) {
  if (!costCents || costCents === 0) return '$0.00';
  return `$${(costCents / 100).toFixed(2)}`;
}

export default function LogsTab() {
  const { getToken } = useAuth();
  const [filters, setFilters] = useState({
    page: 1,
    primitiveId: '',
    statusCode: '',
  });
  const [expandedRow, setExpandedRow] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['api-logs', filters],
    queryFn: async () => {
      const token = await getToken();
      const params = new URLSearchParams();
      params.set('page', filters.page);
      params.set('per_page', '50');
      if (filters.primitiveId) params.set('primitive_id', filters.primitiveId);
      if (filters.statusCode) params.set('status_code', filters.statusCode);

      const res = await fetch(`${API_BASE}/usage/logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch logs');
      return res.json();
    },
    staleTime: 30000,
  });

  const logs = data?.logs || [];
  const totalPages = data ? Math.ceil((data.total || 0) / 50) : 1;

  function updateFilter(key, value) {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    setExpandedRow(null);
  }

  function goToPage(page) {
    setFilters(prev => ({ ...prev, page }));
    setExpandedRow(null);
  }

  return (
    <div className="logs-tab">
      <style>{`
        .logs-tab {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .logs-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--border-primary, #1e2128);
        }
        .logs-header h2 {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
        }
        .logs-header p {
          font-size: 13px;
          color: var(--text-secondary, #8b919e);
          margin: 4px 0 0;
        }
        .logs-filters {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        .filter-select {
          padding: 8px 12px;
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-primary, #1e2128);
          border-radius: 6px;
          color: var(--text-primary, #f0f2f5);
          font-size: 13px;
          min-width: 160px;
        }
        .filter-select:focus {
          outline: none;
          border-color: var(--accent-primary, #6366f1);
        }
        .logs-table-container {
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-primary, #1e2128);
          border-radius: 12px;
          overflow: hidden;
        }
        .logs-table {
          width: 100%;
          border-collapse: collapse;
        }
        .logs-table th {
          text-align: left;
          padding: 12px 16px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-tertiary, #6b7280);
          background: var(--bg-tertiary, #1a1d24);
          border-bottom: 1px solid var(--border-primary, #1e2128);
        }
        .logs-table td {
          padding: 12px 16px;
          font-size: 13px;
          color: var(--text-primary, #f0f2f5);
          border-bottom: 1px solid var(--border-primary, #1e2128);
        }
        .logs-table tr:hover td {
          background: var(--bg-tertiary, #1a1d24);
        }
        .logs-table tr:last-child td {
          border-bottom: none;
        }
        .code-inline {
          padding: 2px 6px;
          background: var(--bg-primary, #0a0a0f);
          border-radius: 4px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 12px;
        }
        .status-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }
        .status-badge.success {
          background: rgba(16, 185, 129, 0.1);
          color: #10B981;
        }
        .status-badge.warning {
          background: rgba(245, 158, 11, 0.1);
          color: #F59E0B;
        }
        .status-badge.error {
          background: rgba(239, 68, 68, 0.1);
          color: #EF4444;
        }
        .logs-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 16px;
        }
        .pagination-btn {
          padding: 8px 16px;
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-primary, #1e2128);
          border-radius: 6px;
          color: var(--text-primary, #f0f2f5);
          font-size: 13px;
          cursor: pointer;
          transition: all 0.15s;
        }
        .pagination-btn:hover:not(:disabled) {
          border-color: var(--accent-primary, #6366f1);
        }
        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .pagination-info {
          font-size: 13px;
          color: var(--text-secondary, #8b919e);
        }
        .logs-empty {
          padding: 48px;
          text-align: center;
          color: var(--text-secondary, #8b919e);
        }
        .logs-empty h3 {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0 0 8px;
        }
        .logs-empty p {
          margin: 0;
          font-size: 14px;
        }
        .logs-loading {
          padding: 48px;
          text-align: center;
          color: var(--text-secondary, #8b919e);
        }
      `}</style>

      <div className="logs-header">
        <div>
          <h2>Request Logs</h2>
          <p>View every API call made with your keys</p>
        </div>
      </div>

      <div className="logs-filters">
        <select
          className="filter-select"
          value={filters.primitiveId}
          onChange={e => updateFilter('primitiveId', e.target.value)}
        >
          <option value="">All primitives</option>
          {PRIMITIVES.map(p => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filters.statusCode}
          onChange={e => updateFilter('statusCode', e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="2xx">Success (2xx)</option>
          <option value="4xx">Error (4xx)</option>
          <option value="5xx">Server Error (5xx)</option>
        </select>
      </div>

      {isLoading ? (
        <div className="logs-loading">Loading...</div>
      ) : error ? (
        <div className="logs-empty">
          <h3>Error loading logs</h3>
          <p>{error.message}</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="logs-table-container">
          <div className="logs-empty">
            <h3>No requests yet</h3>
            <p>Make your first API call from the Explorer tab.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="logs-table-container">
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Primitive</th>
                  <th>Status</th>
                  <th>Latency</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)} style={{ cursor: 'pointer' }}>
                    <td>{formatTimestamp(log.created_at)}</td>
                    <td>
                      <code className="code-inline">{log.primitive_id || '—'}</code>
                    </td>
                    <td>
                      <span className={`status-badge ${log.status_code >= 200 && log.status_code < 300 ? 'success' : log.status_code >= 400 && log.status_code < 500 ? 'warning' : 'error'}`}>
                        {log.status_code}
                      </span>
                    </td>
                    <td>{log.response_time_ms != null ? `${log.response_time_ms}ms` : '—'}</td>
                    <td>{formatCost(log.cost_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="logs-pagination">
            <button
              className="pagination-btn"
              disabled={filters.page <= 1}
              onClick={() => goToPage(filters.page - 1)}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {filters.page} of {totalPages}
            </span>
            <button
              className="pagination-btn"
              disabled={filters.page >= totalPages}
              onClick={() => goToPage(filters.page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
